import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ShopifyImportResult,
  ShopifyProduct,
  ShopifyProductMedia,
  ShopifyProductPage,
} from '../models/shopify.models';
import { GoogleTagManagerConstants, LocalStorageKeys } from 'src/app/core/constants/constants';
import { sendGoogleTagManagerEvent } from 'src/app/core/helpers/helper';
import { LocalStorageService } from 'src/app/core/shared-services/local-storage.service';
import { AppBootstrapService } from 'src/app/core/shared-services/app-bootstrap.service';
import { CommonService } from 'src/app/core/shared-services/common.service';
import { OnboardingService } from 'src/app/core/shared-services/onboarding.service';
import { UserAccountService } from 'src/app/modules/main/user-accounts/useraccount.service';
import { ShopifySignUpRequest, ShopifySignUpResponse } from 'src/app/core/grpc-models/Account_pb';
import { AuthSessionService } from 'src/app/core/shared-services/auth-session.service';
import { isShopifyEmbeddedContext } from 'src/app/core/helpers/shopify-context.helper';

interface ShopifyBridge {
  idToken?: () => Promise<string>;
  auth?: { idToken?: () => Promise<string> };
  config?: { shop?: string };
  shop?: string;
}

interface ShopifyGraphQlProduct {
  id: string;
  title?: string;
  handle?: string;
  description?: string;
  featuredImage?: { url?: string };
  media?: { nodes?: ShopifyGraphQlMedia[] };
}

interface ShopifyGraphQlMedia {
  id: string;
  mediaContentType?: string;
  alt?: string;
  image?: { url?: string; width?: number; height?: number };
  preview?: { image?: { url?: string } };
  sources?: Array<{ url?: string; width?: number; height?: number }>;
  originUrl?: string;
}

interface ShopifyProductsResponse {
  data?: {
    products?: {
      nodes?: ShopifyGraphQlProduct[];
      pageInfo?: { hasNextPage?: boolean; endCursor?: string | null };
    };
  };
}

export interface ShopifyBillingResponse {
  success: boolean;
  message?: string;
  confirmationUrl?: string;
  shopDomain?: string;
  shopifyChargeId?: string;
  billingId?: number;
}

@Injectable({ providedIn: 'root' })
export class ShopifyEmbeddedService {
  bootstrapError = '';
  private readonly shopifyOfflineAuthorizationRequired = 'SHOPIFY_OFFLINE_AUTH_REQUIRED';

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly userAccountService: UserAccountService,
    private readonly appBootstrapService: AppBootstrapService,
    private readonly commonService: CommonService,
    private readonly onboardingService: OnboardingService,
    private readonly localStorageService: LocalStorageService,
    private readonly authSessionService: AuthSessionService,
  ) {}

  get isEmbedded(): boolean {
    return isShopifyEmbeddedContext();
  }

  get postAuthLandingRoute(): string {
    return this.isEmbedded ? '/inspirations' : '/home';
  }

  get currentShopDomain(): string {
    return this.shopDomain;
  }

  async getCurrentIdToken(): Promise<string> {
    return this.waitForIdToken();
  }

  async bootstrapInstallation(): Promise<void> {
    if (!this.isEmbedded) return;
    try {
      const idToken = await this.waitForIdToken();
      await this.request('POST', '/api/shopify/bootstrap', undefined, undefined, false, idToken);
      if (!this.authSessionService.hasToken()) {
        await this.tryAutoLogin(idToken);
      }
      this.bootstrapError = '';
    } catch (error) {
      this.bootstrapError = error instanceof Error
        ? error.message
        : 'Shopify could not authenticate this installation.';
    }
  }

  // Zero-click login/signup for embedded sessions: shopifySignUp already does
  // login-if-linked / signup-if-new, so bootstrap can silently call it with the same
  // id token instead of making the merchant click "Continue with Shopify" themselves.
  // A failure here (e.g. offline auth still pending) just leaves no token set, and the
  // merchant falls back to the existing manual flow via route guards.
  private async tryAutoLogin(idToken: string): Promise<void> {
    const request = new ShopifySignUpRequest();
    request.setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    request.setRedirecturl(`${window.location.origin}/shopify-auth`);
    request.setIdtoken(idToken);
    request.setShop(this.currentShopDomain);

    const response = await this.userAccountService.shopifySignUp(request);
    if (!response?.model?.issuccess || response?.model?.message?.value === this.shopifyOfflineAuthorizationRequired) {
      return;
    }

    await this.completeXoopahLogin(response, 'login');
  }

  // Shared with ShopifyAuthComponent's manual callback path so both the automatic
  // (bootstrap) and explicit (button click) logins populate the session identically.
  async completeXoopahLogin(response: ShopifySignUpResponse.AsObject, mode: 'login' | 'signup'): Promise<void> {
    this.authSessionService.setToken(response.token);
    localStorage.setItem(LocalStorageKeys.businessId, response.businessid?.toString() || '');
    localStorage.setItem(LocalStorageKeys.userId, response.userid?.toString() || '');
    localStorage.setItem(LocalStorageKeys.userEmail, response.businessemail?.value || '');
    localStorage.setItem(LocalStorageKeys.tawkToUserHash, response.tawktohash?.value || '');
    localStorage.removeItem(LocalStorageKeys.isTemporaryAccount);
    localStorage.removeItem('lastGuestRoute');
    localStorage.removeItem('scraperedjson');

    const packageStatusPromise = this.appBootstrapService.getPackageStatus();
    const onboardingPromise = this.appBootstrapService.getBusinessOnboardingDetails();

    this.commonService.setPackageStatus(await packageStatusPromise);
    this.commonService.businessSettingsReady$.next();

    const onboardingResponse = await onboardingPromise;
    this.onboardingService.isOnboardingCompleted = onboardingResponse?.isonboardingcompleted?.value || false;

    sendGoogleTagManagerEvent(
      [
        GoogleTagManagerConstants.CustomTriggerEvents.MXPRegisterUserProfile,
        GoogleTagManagerConstants.CustomTriggerEvents.MXPRegisterSuperProperties,
      ],
      [
        [GoogleTagManagerConstants.DataLayerVars.BusinessId, response.businessid],
        [GoogleTagManagerConstants.DataLayerVars.BusinessName, response.businessname?.value ?? null],
        [GoogleTagManagerConstants.DataLayerVars.BusinessEmail, response.businessemail?.value ?? null],
      ],
    );

    const events = mode === 'signup'
      ? [
          GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserSignUpSuccessful,
          GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserLoginSuccessful,
        ]
      : [GoogleTagManagerConstants.CustomTriggerEvents.BusinessUserLoginSuccessful];
    sendGoogleTagManagerEvent(events);

    if (onboardingResponse?.isonboardingcompleted?.value) {
      this.localStorageService.setItem(LocalStorageKeys.IsOnboardingCompleted, true);
      await this.router.navigate([this.postAuthLandingRoute]);
      return;
    }

    if (onboardingResponse?.isscrapingcompleted?.value) {
      this.localStorageService.setItem(LocalStorageKeys.IsOnboardingCompleted, false);
      await this.router.navigate(['/onboarding/business-details']);
      return;
    }

    const firstName = response.userfirstname?.value || response.businessname?.value || '';
    this.localStorageService.setItem(LocalStorageKeys.userFirstName, firstName);
    await this.router.navigate(['/web-scrapper'], { queryParams: { name: firstName } });
  }

  async ensureConnected(): Promise<void> {
    const connection = await this.request<{ connected: boolean }>('GET', '/api/shopify/connection');
    if (!connection.connected) {
      await this.request('POST', '/api/shopify/connect');
    }
  }

  async getProducts(search = '', cursor: string | null = null, pageSize = 20): Promise<ShopifyProductPage> {
    let params = new HttpParams().set('pageSize', pageSize);
    if (search.trim()) params = params.set('search', search.trim());
    if (cursor) params = params.set('cursor', cursor);

    const response = await this.request<ShopifyProductsResponse>('GET', '/api/shopify/products', undefined, params);
    const connection = response.data?.products;
    return {
      products: (connection?.nodes ?? []).map((product) => this.mapProduct(product)),
      hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
      endCursor: connection?.pageInfo?.endCursor ?? null,
    };
  }

  async importProduct(product: ShopifyProduct): Promise<ShopifyImportResult> {
    return this.request<ShopifyImportResult>('POST', '/api/shopify/import-product', {
      shop: this.shopDomain,
      productGid: product.id,
      shopifyProductId: product.id,
      title: product.title,
      description: product.description,
      media: product.media,
    });
  }

  createBillingSubscription(packageId: number, returnUrl: string, couponCode = ''): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/subscription', {
      packageId,
      returnUrl,
      couponCode,
    });
  }

  completeBillingSubscription(billingChargeId: number, shopifyChargeId = ''): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/subscription/complete', {
      billingChargeId,
      shopifyChargeId,
    });
  }

  createBillingAddon(addonPackageId: number, amount: number, credits: number, returnUrl: string): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/addon', {
      addonPackageId,
      amount,
      credits,
      returnUrl,
    });
  }

  completeBillingAddon(billingChargeId: number, shopifyChargeId = ''): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/addon/complete', {
      billingChargeId,
      shopifyChargeId,
    });
  }

  cancelBillingSubscription(prorate = false): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/subscription/cancel', { prorate });
  }

  syncBillingStatus(): Promise<ShopifyBillingResponse> {
    return this.request<ShopifyBillingResponse>('POST', '/api/shopify/billing/sync');
  }

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
    params?: HttpParams,
    requireXoopahToken = true,
    suppliedIdToken?: string,
  ): Promise<T> {
    const xoopahToken = this.authSessionService.getToken();
    if (requireXoopahToken && !xoopahToken) throw new Error('Sign in to Xoopah before connecting Shopify.');

    const idToken = suppliedIdToken ?? await this.getIdToken();
    if (!idToken) throw new Error('Shopify could not verify this embedded session. Reopen Xoopah from Shopify Admin.');

    let headers = new HttpHeaders({ 'X-Shopify-Id-Token': idToken });
    if (xoopahToken) headers = headers.set('Authorization', `Bearer ${xoopahToken}`);
    if (this.shopDomain) headers = headers.set('X-Shopify-Shop-Domain', this.shopDomain);

    return firstValueFrom(this.http.request<T>(method, `${environment.serverURL}${path}`, { body, headers, params }));
  }

  private async getIdToken(): Promise<string> {
    const bridge = this.bridge;
    if (bridge?.auth?.idToken) return bridge.auth.idToken();
    if (bridge?.idToken) return bridge.idToken();
    return '';
  }

  private async waitForIdToken(): Promise<string> {
    for (let attempt = 0; attempt < 50; attempt += 1) {
      const token = await this.getIdToken();
      if (token) return token;
      await new Promise<void>((resolve) => window.setTimeout(resolve, 100));
    }
    throw new Error('Shopify App Bridge did not initialize.');
  }

  private get bridge(): ShopifyBridge | undefined {
    return (globalThis as typeof globalThis & { shopify?: ShopifyBridge }).shopify;
  }

  private get shopDomain(): string {
    const bridgeShop = this.bridge?.config?.shop ?? this.bridge?.shop;
    if (bridgeShop) return bridgeShop;
    return new URLSearchParams(window.location.search).get('shop') ?? '';
  }

  private mapProduct(product: ShopifyGraphQlProduct): ShopifyProduct {
    const media = (product.media?.nodes ?? []).map((item) => this.mapMedia(item)).filter((item): item is ShopifyProductMedia => !!item);
    return {
      id: product.id,
      title: product.title || 'Untitled product',
      handle: product.handle || '',
      description: product.description || '',
      imageUrl: product.featuredImage?.url || media[0]?.previewUrl || '',
      media,
    };
  }

  private mapMedia(media: ShopifyGraphQlMedia): ShopifyProductMedia | null {
    if (media.image?.url) {
      return { id: media.id, type: 'image', url: media.image.url, previewUrl: media.image.url, alt: media.alt || '', width: media.image.width, height: media.image.height };
    }
    const source = media.sources?.find((item) => !!item.url);
    if (source?.url) {
      return { id: media.id, type: 'video', url: source.url, previewUrl: media.preview?.image?.url || '', alt: media.alt || '', width: source.width, height: source.height };
    }
    if (media.originUrl) {
      return { id: media.id, type: 'external_video', url: media.originUrl, previewUrl: media.preview?.image?.url || '', alt: media.alt || '' };
    }
    return null;
  }
}
